<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useForm } from '@inertiajs/vue3'
import Modal from '@pages/Shared/Modal.vue'
import Spinner from '@pages/Shared/ui/Spinner.vue'
import Input from '@pages/Shared/form/Input.vue'
import MultiSelect from '@pages/Shared/form/MultiSelect.vue'

const props = defineProps({
  modelValue: Boolean,
  title:      String,
  status:     String,
  subject:    { type: Object, default: null },
  sections:   { type: Array,  default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'submitted'])

const form = useForm({
  _method:  null,
  id:       null,
  name:     '',
  arabic:   '',
  sections: [],
  classes:  [],
})

const selectedSections = ref([])
const selectedClasses  = ref([])
const classOptions     = ref([])

const isViewMode = computed(() => props.status === 'view')

const sectionSelectOptions = computed(() =>
  (props.sections || []).map(s => ({ label: s.name, value: s.id }))
)

watch(selectedSections, (sectionIds) => {
  const ids = sectionIds ?? []

  if (!ids.length) {
    classOptions.value    = []
    selectedClasses.value = []
    return
  }

  const sections = (props.sections || []).filter(sec => ids.includes(sec.id))

  classOptions.value = sections
    .flatMap(sec => sec.classes || [])
    .map(c => ({ label: c.name, value: c.id }))

  selectedClasses.value = selectedClasses.value.filter(c =>
    classOptions.value.some(co => co.value === c)
  )
}, { immediate: true, deep: true })

watch(() => props.modelValue, async (open) => {
  if (!open) return
  await nextTick()

  if (props.status === 'create') {
    form.reset()
    form.clearErrors()
    selectedSections.value = []
    selectedClasses.value  = []
    return
  }

  if (!props.subject) return

  form.clearErrors()
  form.id     = props.subject.id     ?? null
  form.name   = props.subject.name   ?? ''
  form.arabic = props.subject.arabic ?? ''
  selectedSections.value = (props.subject.sections || []).map(s => s.id)
  selectedClasses.value  = (props.subject.classes  || []).map(c => c.id)
}, { immediate: true })

const handleSubmit = () => {
  form.sections = [...selectedSections.value]
  form.classes  = [...selectedClasses.value]
  emit('submitted', form)
}

const handleModalClose = () => {
  form.reset()
  form.clearErrors()
  selectedSections.value = []
  selectedClasses.value  = []
  emit('update:modelValue', false)
}
</script>

<template>
  <Modal
    :model-value="modelValue"
    :title="title"
    @update:modelValue="emit('update:modelValue', $event)"
    @close="handleModalClose"
  >
    <form class="p-4 md:p-5" @submit.prevent="handleSubmit">
      
      <Input 
        v-model="form.name"
        type="text" 
        label="Name" 
        placeholder="Subject Name" 
        :error="form.errors.name" 
        :disabled="isViewMode"
        class="mb-4"
      />

      <Input 
        v-model="form.arabic"
        type="text" 
        label="Arabic" 
        placeholder="Arabic Name" 
        :error="form.errors.arabic" 
        :disabled="isViewMode"
        class="mb-4"
      />

      <MultiSelect 
        v-model="selectedSections" 
        :options="sectionSelectOptions" 
        label="Sections"
        placeholder="Select sections"
        :disabled="isViewMode" 
        class="mb-4"
      />

      <MultiSelect 
        v-model="selectedClasses" 
        :options="classOptions"  
        label="Classes"
        placeholder="Select class(es)"
        :disabled="isViewMode" 
        class="mb-4"
      />

      <button
        v-if="!isViewMode"
        type="submit"
        class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold
          transition flex justify-center items-center gap-2"
        :class="status === 'edit'
          ? 'bg-green-600 hover:bg-green-700'
          : 'bg-primary-700 hover:bg-primary-800'"
        :disabled="form.processing"
      >
        <Spinner v-if="form.processing" :size="14" />
        {{ status === 'edit' ? 'Update Subject' : 'Add Subject' }}
      </button>
    </form>
  </Modal>
</template>